import { HttpClient } from '@angular/common/http';
import { TranslateDefaultParser, TranslateLoader, TranslateParser } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Observable, Observer } from 'rxjs';

/**
 * Keeps compatibility with legacy placeholders like {0} and {name}.
 */
export class CoreLegacyTranslateParser extends TranslateDefaultParser {
  interpolate(expr: string | ((...args: any[]) => any), params?: any): string | undefined {
    const interpolated = super.interpolate(expr, params);
    if (typeof interpolated !== 'string' || !params) return interpolated as string | undefined;

    return interpolated.replace(/\{([^{}]+)\}/g, (match: string, key: string) => {
      const value = this._resolveParam(params, key.trim());
      return value === undefined || value === null ? match : String(value);
    });
  }

  private _resolveParam(params: any, key: string): any {
    if (Array.isArray(params) && /^\d+$/.test(key)) return params[Number(key)];
    if (params && Object.prototype.hasOwnProperty.call(params, key)) return params[key];
    return undefined;
  }
}

export class CachedCoreTranslateLoader implements TranslateLoader {
  constructor(private httpLoader: TranslateLoader) {}

  public getTranslation(lang: string): Observable<Record<string, unknown>> {
    return new Observable((observer: Observer<Record<string, unknown>>) => {
      const storageKey = `arkik_i18n_${lang}`;

      // 1. Paso síncrono (0 ms): entrega el caché local de inmediato
      const cachedData = localStorage.getItem(storageKey);
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          observer.next(parsed);
        } catch (e) {
          console.warn('[CachedCoreTranslateLoader] Error al parsear JSON local', e);
        }
      }

      // 2. Paso en segundo plano: verifica la red con el TranslateHttpLoader existente
      this.httpLoader.getTranslation(lang).subscribe({
        next: (freshTranslations) => {
          const freshStr = JSON.stringify(freshTranslations);

          // Si el archivo en el servidor cambió respecto a la copia local
          if (freshStr !== cachedData) {
            try {
              localStorage.setItem(storageKey, freshStr);
            } catch (err) {
              console.error('[CachedCoreTranslateLoader] Error guardando en localStorage', err);
            }

            // Emite la versión actualizada para refrescar la UI
            observer.next(freshTranslations);
          }
          observer.complete();
        },
        error: (err) => {
          console.error('[CachedCoreTranslateLoader] Error de red en traducciones', err);
          // Si falló la red pero ya emitimos desde localStorage, la app sigue funcionando
          if (cachedData) {
            observer.complete();
          } else {
            observer.error(err);
          }
        },
      });
    });
  }
}

export function createCoreTranslateLoader(http: HttpClient): TranslateLoader {
  const baseLoader = new TranslateHttpLoader(http, './assets/I18N/locale-', '.json');
  return new CachedCoreTranslateLoader(baseLoader);
}

export function createCoreTranslateParser(): TranslateParser {
  return new CoreLegacyTranslateParser();
}

export function registerLibTranslations(
  translate: { setTranslation: (lang: string, translations: object, shouldMerge?: boolean) => void },
  translations: Record<string, object>,
): void {
  Object.entries(translations).forEach(([lang, dict]) => {
    const normalized = lang.toLowerCase().replace('-', '_');
    const baseLang = normalized.split('_')[0];

    // Register canonical key from library translations.
    translate.setTranslation(lang, dict, true);

    // Register normalized/base aliases so regional variants (e.g. es-MX, en-US)
    // can resolve without duplicating keys in JSON runtime assets.
    if (normalized !== lang) translate.setTranslation(normalized, dict, true);
    if (baseLang !== lang && baseLang !== normalized) translate.setTranslation(baseLang, dict, true);

    if (baseLang === 'es') {
      translate.setTranslation('es_mx', dict, true);
      translate.setTranslation('es-MX', dict, true);
      translate.setTranslation('es-mx', dict, true);
    }

    if (baseLang === 'en') {
      translate.setTranslation('en_us', dict, true);
      translate.setTranslation('en-US', dict, true);
      translate.setTranslation('en-us', dict, true);
    }
  });
}

export function resolveCoreTranslateLang(rawLang?: string | null): string {
  if (!rawLang) return 'en';

  const normalized = rawLang.toLowerCase().replace('-', '_');
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('fr')) return 'fr';
  if (normalized.startsWith('pl')) return 'pl';
  if (normalized.startsWith('cs')) return 'cs';
  if (normalized.startsWith('de')) return 'de';
  return 'en';
}
